/* eslint-disable no-console */
/* eslint-disable indent */
import React, { useCallback, useMemo } from "react";
import screenmediaLogo from "../../img/screenmedia-logo.png";
import visionaLogo from "../../img/visiona-logo.png";
import wecastLogo from "../../img/wecast-logo.png";
import { getLocale } from "../../utils/getLocale";
import { Document, Page, Text, View, Image, Link } from "@react-pdf/renderer";
import styles from "./stylesPdfCotizacion";

const today = new Date();
const month = String(today.getMonth() + 1).padStart(2, "0");
const day = String(today.getDate()).padStart(2, "0");
const date = `${day}-${month}-${today.getFullYear()}`;
const hour = String(today.getHours()).padStart(2, "0");
const minute = String(today.getMinutes()).padStart(2, "0");

const PdfCotizacion = ({
  ascensoresSeleccionados = 0,
  audienciaSeleccionadaAscensor = 0,
  audienciaSeleccionadaCowork = 0,
  audienciaSeleccionadaEspera = 0,
  audienciaSeleccionadaTotal = 0,
  audienciaSeleccionadaTotems = 0,
  campana = "",
  cliente = "",
  codigoGenerado,
  coworkSeleccionados = 0,
  cpmAscensoresFinal = 0,
  cpmCoworkFinal = 0,
  cpmEsperaFinal = 0,
  cpmTotalFinal = 0,
  cpmTotemsFinal = 0,
  descuentoGlobal = 0,
  duracion = 0,
  esperaSeleccionados = 0,
  horasFinal = 0,
  impactosTotalAscensores = 0,
  impactosTotalCowork = 0,
  impactosTotalEspera = 0,
  impactosTotalTotems = 0,
  impactosTotales = 0,
  impactosEfectivosPoisson = { ascensores: 0, totems: 0, cowork: 0, espera: 0, total: 0 },
  intervalos = [],
  inversionAscensoresFinal = 0,
  inversionCoworkFinal = 0,
  inversionEsperaFinal = 0,
  inversionFinalTotal = 0,
  inversionTotemsFinal = 0,
  isProgrammaticSelected,
  nombresEdificiosNoSeleccionados = [],
  nombresEdificiosSeleccionadosAscensores = [],
  nombresEdificiosSeleccionadosCowork = [],
  nombresEdificiosSeleccionadosEspera = [],
  nombresEdificiosSeleccionadosTotems = [],
  includeInstallingBuildings = false,
  options = [],
  pais = "Chile",
  plazo = 0,
  salidasAscensores = 0,
  salidasCowork = 0,
  salidasEspera = 0,
  salidasTotales = 0,
  salidasTotems = 0,
  tarifaFriaAscensores = 0,
  tarifaFriaCowork = 0,
  tarifaFriaEspera = 0,
  tarifaFriaTotal = 0,
  tarifaFriaTotems = 0,
  totemsSeleccionados = 0,
  usuarioApellido,
  usuarioNombre,
  frecuencia,
  manualDiscountAmount = 0,
  finalAmount = 0,
  manualDiscountPercentage = 0,
  selectedMoneda = "",
  edificios = [],
}) => {
  const monedaSimbolo = useCallback(
    (pais) => {
      if (selectedMoneda === "US$") return "US$ ";
      return (
        {
          Chile: "$ ",
          Perú: "S/ ",
          Uruguay: "$ ",
        }[pais] || "CLP "
      );
    },
    [selectedMoneda]
  );

  const ciudad = useCallback(
    (pais) =>
      ({
        Chile: "Santiago",
        Perú: "Lima",
        Uruguay: "Montevideo",
      })[pais] || "Lima",
    []
  );

  const operacionTextual = useCallback(
    (pais) =>
      ({
        Chile: "WECAST",
        Perú: "VISIONA",
        Uruguay: "SCREENMEDIA",
      })[pais] || "VISIONA",
    []
  );

  const monedaTextual = useCallback(
    (pais) => {
      if (selectedMoneda === "US$") return "US$";
      return (
        {
          Chile: "CLP",
          Perú: "PEN",
          Uruguay: "URU",
        }[pais] || "CLP"
      );
    },
    [selectedMoneda]
  );

  const mailTextual = useCallback(
    (pais) =>
      ({
        Chile: "hola@wecast.cl",
        Perú: "hola@visiona.pe",
        Uruguay: "hola@screenmedia.com.uy",
      })[pais] || "hola@visiona.pe",
    []
  );

  const logo = useMemo(
    () =>
      ({
        Chile: wecastLogo,
        Perú: visionaLogo,
        Uruguay: screenmediaLogo,
      })[pais] || visionaLogo,
    [pais]
  );

  const totalPantallas = ascensoresSeleccionados + totemsSeleccionados + coworkSeleccionados + esperaSeleccionados;
  const nombreUsuario = `${usuarioNombre} ${usuarioApellido}`;
  const numberOfColumns = 7;
  const cellWidth = `${100 / numberOfColumns}%`;

  // Formatear horas: sin decimales si es entero, con 1 decimal si tiene .5
  const formatHoras = (horas) => {
    if (!horas) return "-";
    const hasDecimal = horas % 1 !== 0;
    return horas.toLocaleString(getLocale(pais), {
      minimumFractionDigits: hasDecimal ? 1 : 0,
      maximumFractionDigits: hasDecimal ? 1 : 0,
    });
  };

  const ordenarIntervalos = (a, b) => {
    // Validar que ambos elementos sean strings antes de hacer split
    if (typeof a !== "string" || typeof b !== "string") return 0;
    const [horaA, minutoA] = a.split(" - ")[0].split(":").map(Number);
    const [horaB, minutoB] = b.split(" - ")[0].split(":").map(Number);
    if (horaA !== horaB) return horaA - horaB;
    return minutoA - minutoB;
  };

  // const pluralizar = (cantidad, singular, plural) => {
  //   if (cantidad === 0) return "ningún edificio";
  //   return cantidad === 1 ? singular : plural;
  // };

  // Filtrar y ordenar intervalos válidos (solo strings)
  const intervalosValidos = intervalos.filter((i) => typeof i === "string").sort(ordenarIntervalos);

  const aparicionesDiarias = frecuencia && horasFinal && totalPantallas ? frecuencia * horasFinal * totalPantallas : 0;
  const aparicionesTotal = aparicionesDiarias ? aparicionesDiarias * plazo : 0;

  const activeCircuits = [
    {
      name: "ASCENSORES",
      isActive: ascensoresSeleccionados > 0,
      impactos: impactosTotalAscensores,
      efectivos: impactosEfectivosPoisson.ascensores,
      tarifa: tarifaFriaAscensores,
      baseInversion: inversionAscensoresFinal,
      cpm: cpmAscensoresFinal,
    },
    {
      name: "TÓTEMS",
      isActive: totemsSeleccionados > 0,
      impactos: impactosTotalTotems,
      efectivos: impactosEfectivosPoisson.totems,
      tarifa: tarifaFriaTotems,
      baseInversion: inversionTotemsFinal,
      cpm: cpmTotemsFinal,
    },
    {
      name: "ESPERA",
      isActive: esperaSeleccionados > 0,
      impactos: impactosTotalEspera,
      efectivos: impactosEfectivosPoisson.espera,
      tarifa: tarifaFriaEspera,
      baseInversion: inversionEsperaFinal,
      cpm: cpmEsperaFinal,
    },
    {
      name: "COWORK iF",
      isActive: coworkSeleccionados > 0,
      impactos: impactosTotalCowork,
      efectivos: impactosEfectivosPoisson.cowork,
      tarifa: tarifaFriaCowork,
      baseInversion: inversionCoworkFinal,
      cpm: cpmCoworkFinal,
    },
  ].filter((circuit) => circuit.isActive);

  return (
    <Document>
      {(() => {
        // selectedBuildings ya está calculado en tu código (dentro del return)
        const selectedBuildings = edificios.filter(
          (b) =>
            new Set([
              ...nombresEdificiosSeleccionadosAscensores,
              ...nombresEdificiosSeleccionadosTotems,
              ...nombresEdificiosSeleccionadosEspera,
              ...nombresEdificiosSeleccionadosCowork,
            ]).has(b.edificio) &&
            b.estado &&
            (b.estado === "OPERATIVO" ||
              (includeInstallingBuildings && ["INSTALACIÓN", "REACTIVACIÓN"].includes(b.estado)))
        );

        let pageHeight = 800; // Alto mínimo

        if (selectedBuildings.length > 0) {
          const rows = selectedBuildings.length + 2; // header + edificios + subtotal
          const dynamicHeight = 680 + rows * 21 + 100; // base + filas + footer
          pageHeight = Math.max(1000, dynamicHeight);
        }

        return (
          <Page size={[pageHeight, 700]} orientation="landscape" style={styles.page} wrap={pageHeight >= 2200}>
            <View style={[styles.contentContainer, { flex: 1, justifyContent: "flex-start", height: "100%" }]}>
              <Text style={styles.header} fixed>
                ~ CONFIDENCIAL ~
              </Text>
              <Text style={styles.title}>COTIZACIÓN de ESPACIOS PUBLICITARIOS en {operacionTextual(pais)}</Text>
              <Text style={styles.letrachica0}>
                {ciudad(pais)}, {date} @ {hour}:{minute} | Generado por: {nombreUsuario} | Cotización ID:{" "}
                {codigoGenerado}
              </Text>
              {(cliente || campana) && (
                <Text style={[styles.letrachica0, { marginTop: 0, fontWeight: "bold" }]}>
                  {[cliente ? `Cliente: ${cliente}` : null, campana ? `Campaña: ${campana}` : null]
                    .filter(Boolean)
                    .join(" | ")}
                </Text>
              )}

              {/* TABLA COTIZACIÓN */}
              <View style={[styles.table, { width: "100%" }]}>
                <View
                  style={[
                    styles.tableHeaderTipo,
                    {
                      backgroundColor: isProgrammaticSelected ? "#006400" : "#FF8C00",
                      alignSelf: "center",
                    },
                  ]}
                >
                  <Text>TIPO DE COMPRA: {isProgrammaticSelected ? "PROGRAMMATIC" : "DIGITAL"}</Text>
                </View>
                <View style={[styles.tableHeaderRow, styles.tableHeader]}>
                  <View style={[styles.tableCellQuote, { width: cellWidth }]}>
                    <Text style={{ color: "#ffffff" }}>CIRCUITO</Text>
                  </View>
                  <View style={[styles.tableCellQuote, { width: cellWidth, backgroundColor: "#000000" }]}>
                    <Text style={{ color: "#ffffff", fontSize: 8 }}>CONTACTOS{"\n"}TOTALES</Text>
                  </View>
                  <View style={[styles.tableCellQuote, { width: cellWidth, backgroundColor: "#5e35b1" }]}>
                    <Text style={{ color: "#ffffff", fontSize: 8 }}>IMPACTOS{"\n"}EFECTIVOS</Text>
                  </View>
                  <View style={[styles.tableCellQuote, { width: cellWidth }]}>
                    <Text style={{ color: "#ffffff" }}>COTIZACIÓN</Text>
                  </View>
                  <View style={[styles.tableCellQuote, { width: cellWidth, backgroundColor: "#FF0000" }]}>
                    <Text style={{ color: "#ffffff" }}>DESCUENTO</Text>
                  </View>
                  <View style={[styles.tableCellQuote, { width: cellWidth, backgroundColor: "#008000" }]}>
                    <Text style={{ color: "#ffffff" }}>INVERSIÓN</Text>
                  </View>
                  <View
                    style={[styles.tableCellQuote, styles.lastCell, { width: cellWidth, backgroundColor: "#0000FF" }]}
                  >
                    <Text style={{ color: "#ffffff" }}>CPM</Text>
                  </View>
                </View>

                {/* Row-based table: one View row per circuit, spanning values on midRow */}
                {(() => {
                  const fmt = { minimumFractionDigits: 0, maximumFractionDigits: 0 };
                  const fmtCpm = pais === "Perú" ? { minimumFractionDigits: 1, maximumFractionDigits: 1 } : fmt;
                  const sym = monedaSimbolo(pais);
                  const n = activeCircuits.length;
                  const mid = Math.floor((n - 1) / 2);
                  const cell = {
                    paddingVertical: 5,
                    paddingHorizontal: 2,
                    borderLeftWidth: 0.3,
                    borderRightWidth: 0.3,
                    borderBottomWidth: 0.3,
                    borderColor: "#d3d3d3",
                    backgroundColor: "#f8f9fa",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: cellWidth,
                  };
                  const spanCell = (idx) => [cell, { borderBottomWidth: idx < n - 1 ? 0 : 0.3 }];
                  const txt = { fontSize: 9 };

                  return activeCircuits.map((c, idx) => (
                    <View key={idx} style={{ flexDirection: "row" }}>
                      <View style={[cell, { alignItems: "flex-start", paddingHorizontal: 5 }]}>
                        <Text style={txt}>{c.name}</Text>
                      </View>
                      <View style={spanCell(idx)}>
                        <Text style={txt}>
                          {idx === mid ? impactosTotales.toLocaleString(getLocale(pais), fmt) : ""}
                        </Text>
                      </View>
                      <View style={spanCell(idx)}>
                        <Text style={txt}>
                          {idx === mid
                            ? impactosEfectivosPoisson.total
                              ? impactosEfectivosPoisson.total.toLocaleString(getLocale(pais), fmt)
                              : "-"
                            : ""}
                        </Text>
                      </View>
                      <View style={cell}>
                        <Text style={txt}>{`${sym}${c.tarifa.toLocaleString(getLocale(pais), fmt)}`}</Text>
                      </View>
                      <View style={spanCell(idx)}>
                        <Text style={txt}>
                          {idx === mid
                            ? `${(descuentoGlobal * 100).toLocaleString(getLocale(pais), {
                                minimumFractionDigits: 1,
                                maximumFractionDigits: 1,
                              })}%`
                            : ""}
                        </Text>
                      </View>
                      <View style={cell}>
                        <Text style={txt}>
                          {`${sym} ${Math.floor(c.baseInversion).toLocaleString(getLocale(pais), fmt)}`}
                        </Text>
                      </View>
                      <View style={[cell, { borderRightWidth: 0 }]}>
                        <Text style={txt}>{`${sym} ${c.cpm.toLocaleString(getLocale(pais), fmtCpm)}`}</Text>
                      </View>
                    </View>
                  ));
                })()}

                <View style={[styles.tableRow, styles.tableTotal]}>
                  <Text style={[styles.tableCellStart, styles.tableDataTotales, { width: cellWidth }]}>TOTALES</Text>
                  <Text style={[styles.tableCellQuote, styles.tableDataTotales, { width: cellWidth }]}>
                    {impactosTotales.toLocaleString(getLocale(pais), {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }) || "-"}
                  </Text>
                  <Text style={[styles.tableCellQuote, styles.tableDataTotales, { width: cellWidth }]}>
                    {impactosEfectivosPoisson.total
                      ? impactosEfectivosPoisson.total.toLocaleString(getLocale(pais), {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })
                      : "-"}
                  </Text>
                  <Text style={[styles.tableCellQuote, styles.tableDataTotales, { width: cellWidth }]}>
                    {totalPantallas
                      ? `${monedaSimbolo(pais)}${tarifaFriaTotal.toLocaleString(getLocale(pais), {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}`
                      : "-"}
                  </Text>
                  <Text style={[styles.tableCell, styles.tableDataTotales, { width: cellWidth }]}>
                    {(() => {
                      const format = (value) => {
                        const num = value * 100;
                        const hasDecimal = !Number.isInteger(num);
                        return hasDecimal
                          ? num.toLocaleString("es-CL", { minimumFractionDigits: 1, maximumFractionDigits: 1 })
                          : Math.round(num).toLocaleString("es-CL");
                      };

                      if (!descuentoGlobal && manualDiscountPercentage === 0) return "-";

                      const globalStr = descuentoGlobal > 0 ? format(descuentoGlobal) + "%" : null;
                      const manualStr =
                        manualDiscountPercentage > 0 ? format(manualDiscountPercentage / 100) + "%" : null;

                      if (globalStr && manualStr) {
                        return `${globalStr} + ${manualStr}`;
                      }
                      return globalStr || manualStr || "-";
                    })()}
                  </Text>
                  <Text style={[styles.tableCellQuote, styles.tableDataTotales, { width: cellWidth }]}>
                    {`${monedaSimbolo(pais)} ${
                      manualDiscountAmount > 0
                        ? finalAmount.toLocaleString(getLocale(pais), {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })
                        : inversionFinalTotal.toLocaleString(getLocale(pais), {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })
                    }`}
                  </Text>
                  <Text style={[styles.tableCell, styles.tableDataTotales, styles.lastCell, { width: cellWidth }]}>
                    {`${monedaSimbolo(pais)} ${
                      cpmTotalFinal
                        ? cpmTotalFinal.toLocaleString(getLocale(pais), {
                            minimumFractionDigits: pais === "Perú" ? 1 : 0,
                            maximumFractionDigits: pais === "Perú" ? 1 : 0,
                          })
                        : "-"
                    }`}
                  </Text>
                </View>
              </View>

              {/* Párrafo resumen contextual */}
              <View style={{ marginTop: 10, marginBottom: 6, paddingHorizontal: 20 }}>
                <Text style={{ fontSize: 9, lineHeight: 1.3, textAlign: "center", fontWeight: "bold" }}>
                  {(() => {
                    const numEdificios = new Set([
                      ...nombresEdificiosSeleccionadosAscensores,
                      ...nombresEdificiosSeleccionadosTotems,
                      ...nombresEdificiosSeleccionadosEspera,
                      ...nombresEdificiosSeleccionadosCowork,
                    ]).size;

                    // Formatear horario sin el pipe
                    let horarioTexto = "";
                    if (intervalosValidos.length > 0) {
                      horarioTexto = `intervalos seleccionados (${formatHoras(horasFinal)}h)`;
                    } else if (options.length > 0) {
                      const label = options[0].label;
                      if (label.includes("FULL DAY")) {
                        horarioTexto = "FULL DAY entre las 07:00 y 21:00";
                      } else if (label.includes("AM")) {
                        horarioTexto = "AM entre las 07:00 y 14:00";
                      } else if (label.includes("PM")) {
                        horarioTexto = "PM entre las 14:00 y 21:00";
                      } else {
                        horarioTexto = label;
                      }
                    } else {
                      horarioTexto = "horario seleccionado";
                    }

                    const fmt = { minimumFractionDigits: 0, maximumFractionDigits: 0 };
                    const audiencia = audienciaSeleccionadaTotal.toLocaleString(getLocale(pais), fmt);
                    const contactos = Math.round(impactosTotales).toLocaleString(getLocale(pais), fmt);
                    const efectivos = impactosEfectivosPoisson.total
                      ? impactosEfectivosPoisson.total.toLocaleString(getLocale(pais), fmt)
                      : null;

                    const efectivosTxt = efectivos ? ` y ${efectivos} impactos efectivos` : "";
                    return `Tu pauta se emitirá en ${numEdificios} edificios durante ${plazo} días, con ${frecuencia} salidas por hora en horario ${horarioTexto}, conectando con ${audiencia} personas únicas a través de ${totalPantallas} pantallas, generando ${contactos} contactos totales${efectivosTxt}.`;
                  })()}
                </Text>
              </View>

              <View style={styles.hr} />

              <View style={styles.detallesSeccion}>
                <Text style={styles.detallesTitulo}>AUDIENCIA</Text>
                <Text style={styles.detallesText}>
                  {audienciaSeleccionadaTotal.toLocaleString(getLocale(pais), {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }) || "-"}{" "}
                  personas únicas
                </Text>
              </View>

              <View style={styles.detallesSeccion}>
                <Text style={styles.detallesTitulo}>PLAZO DE PAUTA</Text>
                <Text style={styles.detallesText}>{plazo || "-"} días (lunes a viernes)</Text>
              </View>

              <View style={styles.detallesSeccion}>
                <Text style={styles.detallesTitulo}>EXHIBICIÓN</Text>
                <Text style={styles.detallesText}>
                  {intervalosValidos.length > 0
                    ? `Intervalos: ${intervalosValidos.join(" | ")} = ${formatHoras(horasFinal)} horas por día`
                    : options.length > 0
                      ? `${options[0].label} = ${formatHoras(horasFinal)} horas por día`
                      : `No hay opciones seleccionadas, ${formatHoras(horasFinal)} horas por día`}
                </Text>
              </View>

              <View style={styles.detallesSeccion}>
                <Text style={styles.detallesTitulo}>FRECUENCIA</Text>
                {frecuencia ? (
                  <>
                    <Text style={styles.detallesText}>
                      Por hora:{" "}
                      {(frecuencia * totalPantallas).toLocaleString(getLocale(pais), {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}{" "}
                      totales, {frecuencia} reproducciones por pantalla
                    </Text>
                    <Text style={styles.detallesText}>
                      Por día:{" "}
                      {aparicionesDiarias
                        ? aparicionesDiarias.toLocaleString(getLocale(pais), {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          }) + " reproducciones"
                        : "- reproducciones"}
                    </Text>
                    <Text style={styles.detallesText}>
                      Por campaña:{" "}
                      {aparicionesTotal
                        ? aparicionesTotal.toLocaleString(getLocale(pais), {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          }) + " reproducciones"
                        : "- reproducciones"}
                    </Text>
                  </>
                ) : (
                  <Text style={styles.detallesText}>No especificada</Text>
                )}
              </View>

              <View style={styles.detallesSeccion}>
                <Text style={styles.detallesTitulo}>DURACIÓN DEL CONTENIDO</Text>
                <Text style={styles.detallesText}>{duracion || "-"} segundos</Text>
              </View>

              <View style={styles.detallesSeccion}>
                <Text style={styles.detallesTitulo}>INVENTARIO CONTRATADO</Text>

                {/* ← LÍNEA UNIFICADA: cantidad de edificios + pantallas */}
                <Text style={styles.inventariosubTitulo}>
                  {selectedBuildings.length === 0
                    ? "0 edificios seleccionados"
                    : `${selectedBuildings.length} ${selectedBuildings.length === 1 ? "edificio" : "edificios"} seleccionado${selectedBuildings.length === 1 ? "" : "s"}, ${totalPantallas} pantalla${totalPantallas !== 1 ? "s" : ""}`}
                </Text>
                <Text style={[styles.inventarioNombres]}>
                  {[
                    ascensoresSeleccionados ? `${ascensoresSeleccionados} pantallas en ascensores` : null,
                    totemsSeleccionados ? `${totemsSeleccionados} tótems de circulación` : null,
                    esperaSeleccionados ? `${esperaSeleccionados} pantallas de espera` : null,
                    coworkSeleccionados ? `${coworkSeleccionados} pantallas en cowork` : null,
                  ]
                    .filter(Boolean)
                    .join(", ")}{" "}
                  {![ascensoresSeleccionados, totemsSeleccionados, esperaSeleccionados, coworkSeleccionados].some(
                    Boolean
                  ) && "-"}
                </Text>

                {(() => {
                  // === 1. Obtener IDs seleccionados por circuito ===
                  const ascensoresIds = new Set(
                    nombresEdificiosSeleccionadosAscensores
                      .map((name) => {
                        const edificio = edificios.find((b) => b.edificio === name);
                        if (!edificio) return null;
                        if (!edificio.estado) return null;
                        if (edificio.estado === "OPERATIVO") return edificio.id;
                        if (includeInstallingBuildings && ["INSTALACIÓN", "REACTIVACIÓN"].includes(edificio.estado)) {
                          return edificio.id;
                        }
                        return null;
                      })
                      .filter((id) => id !== null)
                  );

                  const totemsIds = new Set(
                    nombresEdificiosSeleccionadosTotems
                      .map((name) => {
                        const edificio = edificios.find((b) => b.edificio === name);
                        if (!edificio) return null;
                        if (!edificio.estado) return null;
                        if (edificio.estado === "OPERATIVO") return edificio.id;
                        if (includeInstallingBuildings && ["INSTALACIÓN", "REACTIVACIÓN"].includes(edificio.estado)) {
                          return edificio.id;
                        }
                        return null;
                      })
                      .filter((id) => id !== null)
                  );

                  const esperaIds = new Set(
                    nombresEdificiosSeleccionadosEspera
                      .map((name) => {
                        const edificio = edificios.find((b) => b.edificio === name);
                        if (!edificio) return null;
                        if (!edificio.estado) return null;
                        if (edificio.estado === "OPERATIVO") return edificio.id;
                        if (includeInstallingBuildings && ["INSTALACIÓN", "REACTIVACIÓN"].includes(edificio.estado)) {
                          return edificio.id;
                        }
                        return null;
                      })
                      .filter((id) => id !== null)
                  );

                  const coworkIds = new Set(
                    nombresEdificiosSeleccionadosCowork
                      .map((name) => {
                        const edificio = edificios.find((b) => b.edificio === name);
                        if (!edificio) return null;
                        if (!edificio.estado) return null;
                        if (edificio.estado === "OPERATIVO") return edificio.id;
                        if (includeInstallingBuildings && ["INSTALACIÓN", "REACTIVACIÓN"].includes(edificio.estado)) {
                          return edificio.id;
                        }
                        return null;
                      })
                      .filter((id) => id !== null)
                  );

                  // === 2. Determinar si hay cowork ===
                  const hasCowork = coworkIds.size > 0;

                  // === 3. Todos los IDs seleccionados (sin duplicar) ===
                  const selectedIds = new Set([...ascensoresIds, ...totemsIds, ...esperaIds, ...coworkIds]);

                  // === 4. Filtrar edificios válidos + seleccionados ===
                  const selectedBuildings = edificios
                    .filter(
                      (b) =>
                        selectedIds.has(b.id) &&
                        b.estado &&
                        (b.estado === "OPERATIVO" ||
                          (includeInstallingBuildings && ["INSTALACIÓN", "REACTIVACIÓN"].includes(b.estado)))
                    )
                    .map((b) => {
                      const asc = ascensoresIds.has(b.id) ? b.pantallas || 0 : 0;
                      const cir = totemsIds.has(b.id) ? b.totems || 0 : 0;
                      const esp = esperaIds.has(b.id) ? b.espera || 0 : 0;
                      const cow = coworkIds.has(b.id) ? b.cowork || 0 : 0;
                      const total = asc + cir + esp + cow;
                      return {
                        nombre: b.edificio,
                        ubicacion: b.ubicacion || "-",
                        direccion: b.direccion || "-",
                        lat: b.lat,
                        lng: b.lng,
                        ascensores: asc > 0 ? asc : "-",
                        circulacion: cir > 0 ? cir : "-",
                        espera: esp > 0 ? esp : "-",
                        cowork: cow > 0 ? cow : "-",
                        total: total > 0 ? total : "-",
                      };
                    })
                    .sort((a, b) => a.nombre.localeCompare(b.nombre));

                  // === 5. Subtotales ===
                  const subtotalAsc = selectedBuildings.reduce((s, b) => s + (parseInt(b.ascensores) || 0), 0);
                  const subtotalCir = selectedBuildings.reduce((s, b) => s + (parseInt(b.circulacion) || 0), 0);
                  const subtotalEsp = selectedBuildings.reduce((s, b) => s + (parseInt(b.espera) || 0), 0);
                  const subtotalCow = selectedBuildings.reduce((s, b) => s + (parseInt(b.cowork) || 0), 0);
                  const totalGeneral = subtotalAsc + subtotalCir + subtotalEsp + subtotalCow;

                  if (selectedBuildings.length === 0) {
                    return <Text style={styles.inventariosubTitulo}>0 edificios seleccionados</Text>;
                  }

                  // === 6. Calcular anchos dinámicos (optimizado para direcciones largas) ===
                  const baseWidth = hasCowork ? 4 : 8;
                  const edificioWidth = hasCowork ? 18 : 22;
                  const totalWidth = hasCowork ? 6 : 11;
                  const colWidth = hasCowork ? 7 : 13;
                  const direccionWidth = hasCowork ? 32 : 34;

                  return (
                    <>
                      <View style={styles.inventarioTable}>
                        {/* Header */}
                        <View style={styles.inventarioHeaderRow}>
                          <Text style={[styles.inventarioHeaderCell, { width: `${baseWidth}%` }]}>#</Text>
                          <Text
                            style={[
                              styles.inventarioHeaderCell,
                              { width: `${edificioWidth}%`, textAlign: "left", paddingLeft: 8 },
                            ]}
                          >
                            Edificio
                          </Text>
                          <Text style={[styles.inventarioHeaderCell, { width: "14%" }]}>Ubicación</Text>
                          <Text style={[styles.inventarioHeaderCell, { width: `${direccionWidth}%` }]}>Dirección</Text>
                          <Text style={[styles.inventarioHeaderCell, { width: `${colWidth}%` }]}>ASC.</Text>
                          <Text style={[styles.inventarioHeaderCell, { width: `${colWidth}%` }]}>CIR.</Text>
                          <Text style={[styles.inventarioHeaderCell, { width: `${colWidth}%` }]}>ESP.</Text>
                          {hasCowork && (
                            <Text style={[styles.inventarioHeaderCell, { width: `${colWidth}%` }]}>COW.</Text>
                          )}
                          <Text style={[styles.inventarioHeaderCell, { width: `${totalWidth}%`, borderRight: 0 }]}>
                            Total
                          </Text>
                        </View>

                        {/* Filas */}
                        {selectedBuildings.map((b, i) => (
                          <View style={i % 2 === 0 ? styles.inventarioRow : styles.inventarioRowAlt} key={i}>
                            <Text style={[styles.inventarioCell, { width: `${baseWidth}%` }]}>{i + 1}</Text>
                            <Text style={[styles.inventarioCellLeft, { width: `${edificioWidth}%` }]}>{b.nombre}</Text>
                            <Text style={[styles.inventarioCellLeft, { width: "12%" }]}>{b.ubicacion}</Text>{" "}
                            <Text style={[styles.inventarioCellLeft, { width: `${direccionWidth}%` }]}>
                              <Link src={`https://www.google.com/maps?q=${b.lat},${b.lng}`}>{b.direccion}</Link>
                            </Text>
                            <Text style={[styles.inventarioCell, { width: `${colWidth}%` }]}>{b.ascensores}</Text>
                            <Text style={[styles.inventarioCell, { width: `${colWidth}%` }]}>{b.circulacion}</Text>
                            <Text style={[styles.inventarioCell, { width: `${colWidth}%` }]}>{b.espera}</Text>
                            {hasCowork && (
                              <Text style={[styles.inventarioCell, { width: `${colWidth}%` }]}>{b.cowork}</Text>
                            )}
                            <Text
                              style={[
                                styles.inventarioCell,
                                styles.inventarioCellBold,
                                { width: `${totalWidth}%`, borderRight: 0 },
                              ]}
                            >
                              {b.total}
                            </Text>
                          </View>
                        ))}

                        {/* Subtotales */}
                        <View style={styles.inventarioFooterRow}>
                          <Text
                            style={[
                              styles.inventarioCell,
                              {
                                width: `${baseWidth + edificioWidth + 12 + direccionWidth}%`,
                                textAlign: "right",
                                paddingRight: 8,
                                borderRight: 0,
                              },
                            ]}
                          />
                          <Text style={[styles.inventarioCell, styles.inventarioCellBold, { width: `${colWidth}%` }]}>
                            {subtotalAsc}
                          </Text>
                          <Text style={[styles.inventarioCell, styles.inventarioCellBold, { width: `${colWidth}%` }]}>
                            {subtotalCir}
                          </Text>
                          <Text style={[styles.inventarioCell, styles.inventarioCellBold, { width: `${colWidth}%` }]}>
                            {subtotalEsp}
                          </Text>
                          {hasCowork && (
                            <Text style={[styles.inventarioCell, styles.inventarioCellBold, { width: `${colWidth}%` }]}>
                              {subtotalCow}
                            </Text>
                          )}
                          <Text
                            style={[
                              styles.inventarioCell,
                              styles.inventarioCellBold,
                              { width: `${totalWidth}%`, borderRight: 0 },
                            ]}
                          >
                            {totalGeneral}
                          </Text>
                        </View>
                      </View>
                    </>
                  );
                })()}
              </View>

              {/* EDIFICIOS NO SELECCIONADOS + línea separadora (solo si existen) */}
              {nombresEdificiosNoSeleccionados.length > 0 && (
                <>
                  <View style={styles.detallesSeccionNoIncluidos}>
                    <Text style={styles.detallesTitulo}>EDIFICIOS DISPONIBLES NO INCLUIDOS EN ESTA PROPUESTA</Text>
                    <Text style={styles.inventarioNombres}>
                      {nombresEdificiosNoSeleccionados.sort((a, b) => a.localeCompare(b)).join(" // ")}
                    </Text>
                  </View>
                </>
              )}

              <View style={styles.hr} />

              <View style={[styles.footer, { marginTop: 0 }]}>
                <Text style={styles.letrachica2}>
                  Propuesta exclusiva para agencia y/o cliente cotizado. Importes expresados en {monedaTextual(pais)} y
                  no incluyen impuestos. Exhibición de lunes a viernes de 7 AM a 9 PM según frecuencia y horario
                  contratado. Propuesta incluye descuentos especiales que no podrán considerarse como antecedente. El
                  cliente podrá cambiar materiales tantas veces como desee. Cotización válida para pautas a emitirse
                  hasta diciembre 2026 inclusive.
                </Text>
                <Image style={styles.image} src={logo} />
                <Text style={styles.mail}>{mailTextual(pais)}</Text>
              </View>
            </View>
          </Page>
        );
      })()}
    </Document>
  );
};

export default PdfCotizacion;
